import { differenceBy, take } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { connect, DataItem } from 'scrivito'

import { LIST_SIZE } from '../../../const'
import { DropZone } from '@/Atomic/Organisms/DropZone'
import {
  SublistBody,
  SublistSearchResult,
} from '@/Atomic/Organisms/RelationalSublist'
import { SublistFooter } from '@/Atomic/Templates/Sublist'
import { errorToast } from '@/Components/Toast'
import { Company } from '@/Data/Company/CompanyDataClass'
import { DataClassName } from '@/Data/const'
import { ContactListRelation } from '@/Data/ContactListRelation/ContactListRelationDataClass'
import { Person } from '@/Data/Person/PersonDataClass'
import { DataScopeOrder } from '@/Data/types'
import { useDataClassIndex } from '@/hooks/useDataClassIndex'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { DataBinary } from '@/utils/dataBinaryToUrl'
import { getDataItemInitials, getDataItemTitle } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'
import { getDataClassIcon } from '@/utils/image/getDataClassIcon'
import { isString } from '@/utils/isString'
import { reportError } from '@/utils/reportError'

export interface StaticSublistContentProps {
  dataItemId: string
  contactEntity: DataClassName.COMPANY | DataClassName.PERSON
  searchContact: string
  order: DataScopeOrder
  onOpenCsvModal: (file?: File) => void
  onOpenDrawer: (person: DataItem) => void
}

export const StaticSublistContent: React.FC<StaticSublistContentProps> =
  connect(
    ({
      dataItemId,
      contactEntity,
      searchContact,
      order,
      onOpenCsvModal,
      onOpenDrawer,
    }) => {
      const { t } = useTranslation()
      const contactDataClass =
        contactEntity === DataClassName.COMPANY ? Company : Person
      const { flashId: flashItemId, flash: flashItemIdNow } =
        useFlashSublistItem()

      const {
        dataItems: relationalItems,
        hasMore,
        loadMore,
        count,
      } = useDataClassIndex(
        ContactListRelation,
        {
          filters: {
            list: dataItemId,
            contactEntity,
            contact_search: searchContact,
          },
          order: [order],
        },
        LIST_SIZE,
      )

      const searchResults = useDataClassIndex(
        contactDataClass,
        { search: searchContact },
        LIST_SIZE,
      )

      const sublistItems = relationalItems.flatMap((relationalItem) => {
        const contactId = relationalItem.get('contact')
        if (!isString(contactId)) return []

        const contactDataItem = contactDataClass.get(contactId)
        if (!contactDataItem) return []

        return {
          id: relationalItem.id(),
          contactId: contactDataItem.id(),
          title: getDataItemTitle(contactDataItem) ?? '',
          subtitle: ensureString(contactDataItem.get('email')),
          image: contactDataItem.get('image') as DataBinary,
          avatarIcon: getDataClassIcon(contactEntity),
          initials: getDataItemInitials(contactDataItem),
          dataItem: contactDataItem,
          connection: relationalItem,
        }
      })

      const sublistSearchItems = searchResults.dataItems.flatMap((el) => ({
        id: el.id(),
        contactId: el.id(),
        title: getDataItemTitle(el) ?? '',
        subtitle: ensureString(el.get('email')),
        image: el.get('image') as DataBinary,
        avatarIcon: getDataClassIcon(contactEntity),
        initials: getDataItemInitials(el),
      }))

      const filteredSearchResults = differenceBy(
        sublistSearchItems,
        sublistItems,
        'contactId',
      )

      const shownSearchResults =
        searchContact && sublistItems.length < LIST_SIZE
          ? take(filteredSearchResults, LIST_SIZE - sublistItems.length)
          : []

      const linkContact = async (contactId: string) => {
        return ContactListRelation.create({
          list: dataItemId,
          contact: contactId,
          contactEntity,
        })
      }

      const linkSearchResult = async (relatedId: string) => {
        try {
          await linkContact(relatedId).then((e) => flashItemIdNow(e.id()))
        } catch (error) {
          errorToast(error as Error)
          reportError(error)
        }
      }

      return (
        <>
          <SublistBody
            items={sublistItems}
            flashItemId={flashItemId}
            onOpenDrawer={(item) => onOpenDrawer(item.dataItem)}
          />
          <SublistSearchResult
            items={shownSearchResults}
            link={linkSearchResult}
          />
          {!searchContact && (
            <SublistFooter
              amount={sublistItems.length}
              total={count}
              labelKey={getEntityLabelKey(contactEntity)}
              onLoadMore={hasMore ? loadMore : undefined}
            />
          )}
          {sublistItems.length === 0 && !searchContact && (
            <DropZone
              onOpen={() => onOpenCsvModal()}
              onFileDrop={(file) => onOpenCsvModal(file)}
              chooseLabel={t('components.csv.inlineDropChoose')}
            />
          )}
        </>
      )
    },
  )
